import React, { Component, RefObject, ReactElement, } from "react";
import "./style.scss";

/**
 * Props interface for the Bitmap component
 * @interface BitmapProps
 * @property {string} src - The source URL of the image to display
 * @property {string} [className] - Optional CSS class for styling
 * @property {string} [alt] - Optional alt text for accessibility
 * @property {boolean} [autocomplete] - Whether to skip the loading animation
 * @property {string[]} [visibleTo] - Optional array of user roles that can see this image
 * @property {() => void} onComplete - Callback function called when image loading completes
 */
export interface BitmapProps {
    src: string;
    className?: string;
    alt?: string;
    autocomplete?: boolean;
    visibleTo?: string[];
    onComplete: () => void; // event called on completion
}

/**
 * State interface for the Bitmap component
 * @interface BitmapState
 * @property {boolean} loading - Whether the image is currently loading
 * @property {HTMLImageElement} image - The HTML Image element instance
 */
interface BitmapState {
    loading: boolean;
    image: HTMLImageElement;
}

/** Interval between animation steps in milliseconds */
const TICK = 150;

/**
 * Resolution steps for the progressive loading animation
 * Uses a Fibonacci-like sequence for smooth progression
 */
const STEPS = [
    0.01,
    0.02,
    0.03,
    0.05,
    0.08,
    0.13,
    0.21,
    0.34,
    0.55,
    0.89,
    1.00,
];

/**
 * A component that displays images with a progressive loading animation effect.
 * The image starts blurry and gradually becomes clearer through multiple resolution steps.
 * 
 * @component
 * @example
 * ```tsx
 * <Bitmap 
 *   src="/path/to/image.png"
 *   className="custom-image"
 *   alt="Description"
 *   onComplete={() => console.log('Image loaded')}
 * />
 * ```
 */
class Bitmap extends Component<BitmapProps, BitmapState> {
    /** Reference to the canvas element */
    private _canvasRef: RefObject<HTMLCanvasElement> = null;
    /** Timer ID for the animation interval */
    private _animateTimerId: number = null;
    /** Current step in the resolution animation sequence */
    private _currentStep = 0;

    constructor(props: BitmapProps) {
        super(props);

        this._canvasRef = React.createRef<HTMLCanvasElement>();
        const loading = !this.props.autocomplete;

        this.state = {
            loading,
            image: new Image(),
        };
    }

    /**
     * Renders the component with a canvas for the image and optional loading indicator
     * @returns {ReactElement} The rendered component
     */
    public render(): ReactElement {
        const { className } = this.props;
        const { loading } = this.state;
        const css = ["__image__", className ? className : null].join(" ").trim();

        return (
            <div className={css}>
                {loading && <div className="progressbar" />}
                <canvas ref={this._canvasRef} />
            </div>
        );
    }

    /**
     * Initiates image loading when component mounts
     */
    public componentDidMount(): void {
        this._loadImage();
    }

    /**
     * Resamples the image at a specific resolution for the loading animation
     * @param {number} resolution - The resolution factor (0-1)
     * @private
     */
    private _resampleImage(resolution: number): void {
        const { image, } = this.state;
        const canvas = this._canvasRef.current;
        if (canvas === null) {
            return;
        }
        const ctx = canvas.getContext("2d");

        const w = image.width;
        const h = image.height;

        const dw = w * resolution;
        const dh = h * resolution;

        // trun off smoothing to ensure it's pixelated
        ctx.imageSmoothingEnabled = false;
        // shrink the image
        ctx.drawImage(image, 0, 0, dw, dh);
        // then draw the above bitmap at then expected image size without resampling
        ctx.drawImage(canvas, 0, 0, dw, dh, 0, 0, w, h);
    }

    /**
     * Cleans up the animation timer
     * @private
     */
    private _clearAnimationTimer = () => {
        if (this._animateTimerId) {
            window.clearInterval(this._animateTimerId);
            this._animateTimerId = null;
        }
    };

    /**
     * Handles the progressive loading animation
     * @private
     */
    private _animate(): void {
        const { onComplete, } = this.props;

        this._clearAnimationTimer();
        this._animateTimerId = window.setInterval(() => {
            if (this._currentStep < STEPS.length) {
                this._resampleImage(STEPS[this._currentStep]);
                this._currentStep++;
            } else {
                this._clearAnimationTimer();
                onComplete && onComplete();
            }
        }, TICK);
    }

    /**
     * Loads the image and sets up the canvas for display
     * Triggers the loading animation or displays the image directly
     * @private
     */
    private _loadImage(): void {
        const { autocomplete, onComplete, src, } = this.props;
        const { image } = this.state;
        const canvas = this._canvasRef.current;
        const ctx = canvas.getContext("2d");

        if (ctx && image) {
            image.onload = () => {
                // resize the canvas element
                const w = image.width;
                const h = image.height;

                // todo: max dimensions
                // make sure width is no larger than container width
                canvas.width = w;
                canvas.height = h;

                if (!autocomplete) {
                    this.setState({
                        loading: false,
                    }, () => this._animate());
                } else {
                    ctx.drawImage(image, 0, 0);
                    onComplete && onComplete();
                }
            };
            image.src = src;
        }
    }
}

export default Bitmap;
